import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { LeagueService, TeamService, ContestantService, PredictionService, SeasonService } from '../../services/firebaseService';
import TeamSelector from '../../components/player/TeamSelector';
import { normalizeToContestantId } from '../../utils/contestantUtils';
import {
  getEliminationTypeDisplayLabel,
  getRequiredPredictionCount,
  isMultiEliminationWeek,
} from '../../utils/predictionUtils';
import { getUserTeamsForLeagueSeason } from '../../utils/teamUtils';

export default function PredictionCollection() {
  const { leagueId, seasonId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [league, setLeague] = useState(null);
  const [season, setSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [userTeamIds, setUserTeamIds] = useState([]);
  
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [submittedByName, setSubmittedByName] = useState('');
  const [predictions, setPredictions] = useState({
    votedOut: '',
    fireMakingLoser: '',
    winner: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Submit Your Predictions';
    return () => {
      document.title = 'Survivor Fantasy League';
    };
  }, []);

  useEffect(() => {
    if (leagueId && seasonId) {
      loadData();
    }
  }, [leagueId, seasonId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load league data
      const leagueData = await LeagueService.getLeague(leagueId);
      if (!leagueData) {
        throw new Error('League not found');
      }
      setLeague(leagueData);

      // Load season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        throw new Error('Season not found');
      }
      setSeason(seasonData);

      // Load teams for this league-season
      const teamsData = await TeamService.getTeamsForLeagueSeason(leagueId, seasonId);
      const sortedTeams = teamsData.sort((a, b) => a.name.localeCompare(b.name));
      setTeams(sortedTeams);

      // Load contestants for this season
      const contestantsData = await ContestantService.getContestantsBySeason(seasonId);
      const sortedContestants = contestantsData.sort((a, b) => a.name.localeCompare(b.name));
      setContestants(sortedContestants);

      // Load weeks that are open for predictions
      const openWeeks = seasonData.weeks?.filter(week => week.isOpenForPredictions) || [];
      setWeeks(openWeeks);

      // Load user's teams if logged in
      if (currentUser && userProfile && userProfile.teams) {
        const claimedTeams = getUserTeamsForLeagueSeason(userProfile.teams, leagueId, seasonId);
        setUserTeamIds(claimedTeams);
        
        // Auto-select team if user has exactly one team
        if (claimedTeams.length === 1) {
          setSelectedTeam(claimedTeams[0]);
        }
      }

      // Pre-fill name if logged in
      if (currentUser && userProfile) {
        setSubmittedByName(userProfile.displayName || currentUser.email?.split('@')[0] || '');
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to load data. Please check the URL and try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentWeek = () => {
    return weeks.find(week => week.weekNumber.toString() === selectedWeek);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    if (typeof timestamp === 'string') return timestamp;
    if (timestamp.seconds) {
      // Firestore timestamp
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    return timestamp.toString();
  };

  const handlePredictionChange = (field, value) => {
    setPredictions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTeam || !selectedWeek) {
      setError('Please select both a team and an episode.');
      return;
    }

    if (!submittedByName.trim()) {
      setError('Please enter your name to confirm your prediction.');
      return;
    }

    const currentWeek = getCurrentWeek();
    if (!currentWeek) {
      setError('Selected episode not found.');
      return;
    }

    // Validate predictions based on week type
    if (currentWeek.isFinale) {
      if (!predictions.votedOut || !predictions.fireMakingLoser || !predictions.winner) {
        setError('Please make all three predictions for the finale.');
        return;
      }
    } else if (isMultiEliminationWeek(currentWeek)) {
      const need = getRequiredPredictionCount(currentWeek);
      if (!predictions.votedOut) {
        setError(
          `Please make all ${need} predictions for this elimination episode.`
        );
        return;
      }
      const votedOutArray = predictions.votedOut.split(',').map(s => s.trim()).filter(s => s);
      if (votedOutArray.length !== need) {
        setError(
          `Please select exactly ${need} contestants (in elimination order).`
        );
        return;
      }
    } else {
      if (!predictions.votedOut) {
        setError('Please make a prediction for who will be voted out.');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError('');

      // Normalize contestant values to IDs (handles both ID and name formats)
      const normalizeContestant = (value) => {
        if (!value) return null;
        return normalizeToContestantId(value, contestants) || value;
      };

      // Prepare prediction data based on week type (using IDs)
      let predictionData = {};
      
      if (currentWeek.isFinale) {
        predictionData = {
          votedOut: normalizeContestant(predictions.votedOut),
          fireMakingLoser: normalizeContestant(predictions.fireMakingLoser),
          winner: normalizeContestant(predictions.winner)
        };
      } else if (isMultiEliminationWeek(currentWeek)) {
        const votedOutArray = predictions.votedOut.split(',').map(s => s.trim()).filter(s => s);
        const normalizedIds = votedOutArray.map(id => normalizeContestant(id)).filter(Boolean);
        predictionData = {
          votedOut: normalizedIds.map((id, idx) => ({
            contestantId: id,
            position: idx + 1,
          })),
        };
      } else {
        predictionData = {
          votedOut: normalizeContestant(predictions.votedOut)
        };
      }

      // Determine submittedBy value
      const submittedBy = currentUser 
        ? currentUser.uid 
        : `Public: ${submittedByName.trim()}`;

      // Check if prediction already exists
      console.log('Checking for existing prediction:', {
        leagueId,
        seasonId,
        teamId: selectedTeam,
        weekNumber: parseInt(selectedWeek)
      });
      
      const existingPrediction = await PredictionService.getTeamPrediction(
        leagueId, 
        seasonId, 
        selectedTeam, 
        parseInt(selectedWeek)
      );

      console.log('Existing prediction found:', existingPrediction);

      if (existingPrediction) {
        // Update existing prediction
        console.log('Updating existing prediction with ID:', existingPrediction.id);
        await PredictionService.updatePrediction(
          existingPrediction.id, 
          {
            predictions: predictionData,
            submittedBy: submittedBy,
            updatedAt: new Date()
          },
          submittedBy,
          currentUser ? currentUser.uid : null
        );
      } else {
        // Create new prediction
        console.log('Creating new prediction');
        await PredictionService.createPrediction(
          {
            leagueId,
            seasonId,
            teamId: selectedTeam,
            weekNumber: parseInt(selectedWeek),
            predictions: predictionData,
            submittedBy: submittedBy,
            isLocked: false
          },
          submittedBy,
          currentUser ? currentUser.uid : null
        );
      }

      setSuccess(true);
      
      // Reset form
      setSelectedTeam('');
      setSelectedWeek('');
      setSubmittedByName('');
      setPredictions({
        votedOut: '',
        fireMakingLoser: '',
        winner: ''
      });

    } catch (error) {
      console.error('Error submitting prediction:', error);
      setError(error.message || 'Failed to submit prediction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading prediction form...</p>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-900">Error</h3>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentWeek = getCurrentWeek();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Submit Your Prediction
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {league?.name} - {season?.name}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Prediction Submitted Successfully!
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  Your prediction has been recorded. You can submit another prediction below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Prediction Form */}
        <div className="bg-white shadow-lg rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Team Selection */}
            <div>
              {currentUser && userTeamIds.length > 0 ? (
                // Logged-in user with claimed teams - use TeamSelector
                <TeamSelector
                  teams={teams}
                  userTeamIds={userTeamIds}
                  selectedTeamId={selectedTeam}
                  onTeamChange={setSelectedTeam}
                  leagueName={league?.name}
                />
              ) : (
                // Logged-out user or no claimed teams - show full team dropdown
                <>
                  <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-2">
                    1. What team are you on?
                  </label>
                  <select
                    id="team"
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    required
                  >
                    <option value="">Select your team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {/* Episode Selection */}
            <div>
              <label htmlFor="episode" className="block text-sm font-medium text-gray-700 mb-2">
                2. Which episode are you predicting?
              </label>
              <select
                id="episode"
                value={selectedWeek}
                onChange={(e) => {
                  setSelectedWeek(e.target.value);
                  // Reset predictions when week changes
                  setPredictions({
                    votedOut: '',
                    fireMakingLoser: '',
                    winner: ''
                  });
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                required
              >
                <option value="">Select an episode</option>
                {weeks.map(week => (
                  <option key={week.weekNumber} value={week.weekNumber}>
                    Week {week.weekNumber} - {formatDate(week.airDate)}
                    {week.isFinale && ' (Finale)'}
                    {isMultiEliminationWeek(week) &&
                      ` (${getEliminationTypeDisplayLabel(week)})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Predictions */}
            {selectedWeek && currentWeek && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  3. Make your predictions:
                </label>
                
                {currentWeek.isFinale ? (
                  // Finale predictions
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="votedOut" className="block text-sm font-medium text-gray-600 mb-1">
                        Who will be voted out?
                      </label>
                      <select
                        id="votedOut"
                        value={predictions.votedOut}
                        onChange={(e) => handlePredictionChange('votedOut', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        required
                      >
                        <option value="">Select contestant</option>
                        {contestants.map(contestant => (
                          <option key={contestant.id} value={contestant.id}>
                            {contestant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="fireMakingLoser" className="block text-sm font-medium text-gray-600 mb-1">
                        Who will lose the fire-making challenge?
                      </label>
                      <select
                        id="fireMakingLoser"
                        value={predictions.fireMakingLoser}
                        onChange={(e) => handlePredictionChange('fireMakingLoser', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        required
                      >
                        <option value="">Select contestant</option>
                        {contestants.map(contestant => (
                          <option key={contestant.id} value={contestant.id}>
                            {contestant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="winner" className="block text-sm font-medium text-gray-600 mb-1">
                        Who will win the season?
                      </label>
                      <select
                        id="winner"
                        value={predictions.winner}
                        onChange={(e) => handlePredictionChange('winner', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                        required
                      >
                        <option value="">Select contestant</option>
                        {contestants.map(contestant => (
                          <option key={contestant.id} value={contestant.id}>
                            {contestant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : isMultiEliminationWeek(currentWeek) ? (
                  <div className="space-y-4">
                    {Array.from(
                      { length: getRequiredPredictionCount(currentWeek) },
                      (_, i) => {
                        const pos = i + 1;
                        const ordinal =
                          pos === 1 ? 'first' : pos === 2 ? 'second' : 'third';
                        return (
                          <div key={pos}>
                            <label
                              htmlFor={`votedOut${pos}`}
                              className="block text-sm font-medium text-gray-600 mb-1"
                            >
                              Vote out #{pos} ({ordinal} person eliminated):
                            </label>
                            <select
                              id={`votedOut${pos}`}
                              value={
                                predictions.votedOut.split(',')[i]?.trim() || ''
                              }
                              onChange={(e) => {
                                const n = getRequiredPredictionCount(currentWeek);
                                const parts = predictions.votedOut
                                  .split(',')
                                  .map(s => s.trim());
                                while (parts.length < n) parts.push('');
                                parts[i] = e.target.value;
                                handlePredictionChange(
                                  'votedOut',
                                  parts.slice(0, n).join(',')
                                );
                              }}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                              required
                            >
                              <option value="">Select contestant</option>
                              {contestants.map(contestant => (
                                <option key={contestant.id} value={contestant.id}>
                                  {contestant.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      }
                    )}
                  </div>
                ) : (
                  // Single elimination prediction
                  <div>
                    <label htmlFor="votedOut" className="block text-sm font-medium text-gray-600 mb-1">
                      Who will be voted out?
                    </label>
                    <select
                      id="votedOut"
                      value={predictions.votedOut}
                      onChange={(e) => handlePredictionChange('votedOut', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                      required
                    >
                      <option value="">Select contestant</option>
                      {contestants.map(contestant => (
                        <option key={contestant.id} value={contestant.id}>
                          {contestant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Name Confirmation - Only show for logged-out users */}
            {!currentUser && (
              <div>
                <label htmlFor="submittedByName" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm your name:
                </label>
                <input
                  type="text"
                  id="submittedByName"
                  value={submittedByName}
                  onChange={(e) => setSubmittedByName(e.target.value)}
                  maxLength={100}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  placeholder="Enter your name"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {submittedByName.length}/100 characters
                </p>
              </div>
            )}


            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting || !selectedTeam || !selectedWeek || (!currentUser && !submittedByName.trim())}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-survivor-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Prediction'}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact your league manager.
          </p>
        </div>
      </div>
    </div>
  );
}
